import { Icon, scaleOpacity } from '@dynamic/ui';
import styled, { css } from 'styled-components';

import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { horizontalDirection } from '#/service/constants';
import { remify } from '#/service/utils';

import { fixFocusInSafari } from '../../styles';
import { StyledInputProps, StyledTextAreaLabel } from '../Form.types';
import { InputLabelProps, StyledIconProps } from './FormInput.types';

export const cssInputBase = (hasRightIcon = false) => css`
  height: 100%;
  width: 100%;
  font: var(--fontStyleCallout);
  box-sizing: border-box;
  color: var(--inputForeground);
  background-color: var(--inputBackground);
  border-radius: var(--inputBorderRadius);
  padding: ${remify(17)} ${remify(hasRightIcon ? 44 : 12)} ${remify(3)}
    ${remify(12)};
`;

interface ContainerProps {
  $width?: string;
}

export const Container = styled.div<ContainerProps>`
  margin: 0 0 ${remify(16)} 0;
  position: relative;
  width: ${({ $width }) => $width || '100%'};
  flex: ${({ $width }) => ($width ? `0 0 ${$width}` : '0 0 100%')};

  @media ${MEDIA_QUERIES.sm} {
    width: 100%;
    flex: 0 0 100%;
  }
`;

export const InputContainer = styled.div`
  position: relative;
  height: ${remify(50)};
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  --leadingIconOpacity: 0.6;
  --trailingIconOpacity: 0.6;
  --labelOpacity: 0.6;

  &:hover {
    --trailingIconOpacity: 1;
    --labelOpacity: 1;
  }

  &:focus-within,
  &.filled {
    --trailingIconOpacity: 1;
    --labelOpacity: 0.6;
  }
`;

export const InputLabel = styled.label<InputLabelProps>`
  position: absolute;
  ${({ $bigLabel }) => !$bigLabel && `top: 0;`}
  left: ${remify(12)};
  right: ${({ $hasRightIcon }) => remify($hasRightIcon ? 44 : 5)};
  padding: ${remify(4)} 0;
  font: ${({ $bigLabel }) =>
    $bigLabel ? `var(--fontStyleCallout)` : `var(--fontStyleCaption1)`};
  color: var(--inputForeground);
  opacity: var(--labelOpacity);
  cursor: text;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

export const StyledIcon = styled(Icon)<StyledIconProps>`
  position: absolute;
  top: ${remify(13)};
  color: var(--inputForeground);

  ${({ position }) => {
    const isRight = position === horizontalDirection.RIGHT;

    return `
      opacity: var(${isRight ? '--trailingIconOpacity' : '--leadingIconOpacity'});
      ${position}: ${remify(12)};
      ${isRight ? 'cursor: pointer;' : ''}`;
  }}
`;

export const StyledInput = styled('input')<StyledInputProps>`
  ${({ $hasRightIcon }) => cssInputBase($hasRightIcon)}
  border: none;

  &:invalid {
    ${({ $touched }) =>
      $touched && `box-shadow: inset 0 0 0 2px var(--inputValidation);`}
  }

  &:focus {
    outline: 1px solid var(--itemFocusFrame);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fixFocusInSafari}
`;

export const TextAreaContainer = styled(InputContainer)`
  position: relative;
  height: ${remify(136)};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const StyledTextArea = styled.textarea<StyledInputProps>`
  ${({ $hasRightIcon }) => cssInputBase($hasRightIcon)}
  border: none;
  min-height: ${remify(136)};
  min-width: 100%;
  padding: ${remify(12)};
  resize: none;

  /*  Firefox scrollbar  */
  scrollbar-color: ${({ theme }) =>
      scaleOpacity(theme.color.inputForeground, 0.6)}
    transparent;

  /* Webkit scrollbars, Chrome, Edge, Safari */
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) =>
      scaleOpacity(theme.color.inputForeground, 0.6)};
    border: 1px solid var(--inputBackground);
    border-radius: 20px;
  }

  &:invalid {
    ${({ $touched }) =>
      $touched && `box-shadow: inset 0 0 0 2px var(--inputValidation);`}
  }

  &:focus {
    outline: 1px solid var(--itemFocusFrame);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fixFocusInSafari}
`;

export const TextAreaLabel = styled.label<StyledTextAreaLabel>`
  position: absolute;
  display: ${({ $hide }) => ($hide ? 'none' : 'flex')};
  margin: ${remify(12)};
  font: var(--fontStyleBody);
  color: var(--inputForeground);
  opacity: var(--labelOpacity);
  cursor: text;
  text-align: left;
  max-width: 100%;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
